/**
 * IMPORTANT: This file is modified from the original shadcn/ui file.
 *            DO NOT OVERWRITE IT WITH THE CLI.
 */

import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '@/utils/cn'

const inputVariants = cva(
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:font-medium file:text-foreground file:text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
  {
    variants: {
      iconPosition: {
        left: 'pl-9',
        right: 'pr-9',
        both: 'pr-9 pl-9',
        none: '',
      },
    },
    defaultVariants: {
      iconPosition: 'none',
    },
  },
)

const iconWrapperVariants = cva(
  '-translate-y-1/2 absolute top-1/2 flex items-center justify-center [&_svg]:size-4',
  {
    variants: {
      position: {
        left: 'left-3',
        right: 'right-3',
      },
    },
  },
)

interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    Omit<VariantProps<typeof inputVariants>, 'iconPosition'> {
  IconLeft?: React.ReactNode
  IconRight?: React.ReactNode
  wrapperClassName?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, IconLeft, IconRight, wrapperClassName, ...props }, ref) => {
    // Determine icon position variant
    const iconPosition =
      IconLeft && IconRight ? 'both' : IconLeft ? 'left' : IconRight ? 'right' : 'none'

    const inputElement = (
      <input
        type={type}
        className={cn(inputVariants({ iconPosition }), className)}
        ref={ref}
        {...props}
      />
    )

    if (IconLeft || IconRight) {
      return (
        <div className={cn('relative w-full', wrapperClassName)}>
          {IconLeft && <div className={iconWrapperVariants({ position: 'left' })}>{IconLeft}</div>}
          {inputElement}
          {IconRight && (
            <div className={iconWrapperVariants({ position: 'right' })}>{IconRight}</div>
          )}
        </div>
      )
    }

    return inputElement
  },
)
Input.displayName = 'Input'

export { Input, inputVariants }
